import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import teamService from '../../services/teamService';
import { followPlayer, unfollowPlayer } from '../coach/coachProfileSlice';

const initialState = {
  // Player teams
  teams: [],
  selectedTeam: null,
  teamPlayers: [],
  playerTeamsLoading: false,
  playerTeamsPagination: {
    currentPage: 1,
    totalPages: 1,
    totalTeams: 0,
    limit: 10,
    hasMore: false,
  },
  // Coach teams
  coachTeams: [],
  selectedCoachTeam: null,
  teamRoster: [],
  rosterTeamInfo: null,
  rosterPagination: {
    currentPage: 1,
    totalPages: 1,
    totalPlayers: 0,
    limit: 20,
    hasMore: false,
  },
  // Coach teams pagination
  teamsPagination: {
    currentPage: 1,
    totalPages: 1,
    totalTeams: 0,
    limit: 10,
    hasMore: false,
  },
  teamsLoading: false,
  loading: false,
  error: null,
};

export const fetchPlayerTeams = createAsyncThunk(
  'team/fetchPlayerTeams',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await teamService.getPlayerTeams(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch teams.'
      );
    }
  }
);

export const loadMorePlayerTeams = createAsyncThunk(
  'team/loadMorePlayerTeams',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await teamService.getPlayerTeams(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to load more teams.'
      );
    }
  }
);

export const fetchTeamPlayers = createAsyncThunk(
  'team/fetchTeamPlayers',
  async (teamId, { rejectWithValue }) => {
    try {
      const data = await teamService.getTeamPlayers(teamId);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch team players.'
      );
    }
  }
);

// Coach team actions
export const fetchCoachTeams = createAsyncThunk(
  'team/fetchCoachTeams',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await teamService.getCoachTeams(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch coach teams.'
      );
    }
  }
);

export const loadMoreCoachTeams = createAsyncThunk(
  'team/loadMoreCoachTeams',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await teamService.getCoachTeams(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to load more teams.'
      );
    }
  }
);

export const fetchCoachTeamRoster = createAsyncThunk(
  'team/fetchCoachTeamRoster',
  async ({ teamId, page = 1, limit = 20, seasonYear = null, filters = null, sortBy = null, sortOrder = null, statsType = null, category = null, scope = null }, { rejectWithValue }) => {
    try {
      const data = await teamService.getCoachTeamRoster(teamId, page, limit, seasonYear, filters, sortBy, sortOrder, statsType, category, scope);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch team roster.'
      );
    }
  }
);

export const loadMoreCoachTeamRoster = createAsyncThunk(
  'team/loadMoreCoachTeamRoster',
  async ({ teamId, page, limit = 20, seasonYear = null, filters = null, sortBy = null, sortOrder = null, statsType = null, category = null, scope = null }, { rejectWithValue }) => {
    try {
      const data = await teamService.getCoachTeamRoster(teamId, page, limit, seasonYear, filters, sortBy, sortOrder, statsType, category, scope);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to load more roster.'
      );
    }
  }
);

const teamSlice = createSlice({
  name: 'team',
  initialState,
  reducers: {
    setSelectedTeam: (state, action) => {
      state.selectedTeam = action.payload;
    },
    setSelectedCoachTeam: (state, action) => {
      state.selectedCoachTeam = action.payload;
    },
    clearTeamError: (state) => {
      state.error = null;
    },
    clearTeamData: (state) => {
      state.teams = [];
      state.selectedTeam = null;
      state.teamPlayers = [];
      state.coachTeams = [];
      state.selectedCoachTeam = null;
      state.teamRoster = [];
      state.teamsPagination = initialState.teamsPagination;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlayerTeams.pending, (state) => {
        state.playerTeamsLoading = true;
        state.error = null;
      })
      .addCase(fetchPlayerTeams.fulfilled, (state, action) => {
        state.playerTeamsLoading = false;
        state.teams = action.payload.teams || action.payload;
        const pagination = action.payload.pagination || {};
        state.playerTeamsPagination = {
          currentPage: pagination.currentPage || 1,
          totalPages: pagination.totalPages || 1,
          totalTeams: pagination.totalTeams || pagination.totalCount || 0,
          limit: pagination.limit || 10,
          hasMore: pagination.currentPage < pagination.totalPages,
        };
        state.error = null;
      })
      .addCase(fetchPlayerTeams.rejected, (state, action) => {
        state.playerTeamsLoading = false;
        state.error = action.payload;
      })
      // Load more player teams
      .addCase(loadMorePlayerTeams.pending, (state) => {
        state.playerTeamsLoading = true;
        state.error = null;
      })
      .addCase(loadMorePlayerTeams.fulfilled, (state, action) => {
        state.playerTeamsLoading = false;
        const newTeams = action.payload.teams || action.payload;
        state.teams = [...state.teams, ...newTeams];
        const pagination = action.payload.pagination || {};
        state.playerTeamsPagination = {
          currentPage: pagination.currentPage || 1,
          totalPages: pagination.totalPages || 1,
          totalTeams: pagination.totalTeams || pagination.totalCount || 0,
          limit: pagination.limit || 10,
          hasMore: pagination.currentPage < pagination.totalPages,
        };
        state.error = null;
      })
      .addCase(loadMorePlayerTeams.rejected, (state, action) => {
        state.playerTeamsLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchTeamPlayers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeamPlayers.fulfilled, (state, action) => {
        state.loading = false;
        state.teamPlayers = action.payload;
        state.error = null;
      })
      .addCase(fetchTeamPlayers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Coach teams
      .addCase(fetchCoachTeams.pending, (state) => {
        state.teamsLoading = true;
        state.error = null;
      })
      .addCase(fetchCoachTeams.fulfilled, (state, action) => {
        state.teamsLoading = false;
        state.coachTeams = action.payload.teams || action.payload;
        const pagination = action.payload.pagination || {};
        state.teamsPagination = {
          currentPage: pagination.currentPage || 1,
          totalPages: pagination.totalPages || 1,
          totalTeams: pagination.totalTeams || pagination.totalCount || 0,
          limit: pagination.limit || 10,
          hasMore: pagination.currentPage < pagination.totalPages,
        };
        state.error = null;
        // Auto-select first team if none selected
        if (!state.selectedCoachTeam && state.coachTeams.length > 0) {
          state.selectedCoachTeam = state.coachTeams[0];
        }
      })
      .addCase(fetchCoachTeams.rejected, (state, action) => {
        state.teamsLoading = false;
        state.error = action.payload;
      })
      // Load more coach teams
      .addCase(loadMoreCoachTeams.pending, (state) => {
        state.teamsLoading = true;
        state.error = null;
      })
      .addCase(loadMoreCoachTeams.fulfilled, (state, action) => {
        state.teamsLoading = false;
        const newTeams = action.payload.teams || action.payload;
        state.coachTeams = [...state.coachTeams, ...newTeams];
        const pagination = action.payload.pagination || {};
        state.teamsPagination = {
          currentPage: pagination.currentPage || 1,
          totalPages: pagination.totalPages || 1,
          totalTeams: pagination.totalTeams || pagination.totalCount || 0,
          limit: pagination.limit || 10,
          hasMore: pagination.currentPage < pagination.totalPages,
        };
        state.error = null;
      })
      .addCase(loadMoreCoachTeams.rejected, (state, action) => {
        state.teamsLoading = false;
        state.error = action.payload;
      })
      // Coach team roster
      .addCase(fetchCoachTeamRoster.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCoachTeamRoster.fulfilled, (state, action) => {
        state.loading = false;
        state.teamRoster = action.payload.players || action.payload.roster || [];
        state.rosterTeamInfo = action.payload.team || null;
        state.rosterPagination = {
          currentPage: action.payload.pagination?.currentPage || action.payload.currentPage || 1,
          totalPages: action.payload.pagination?.totalPages || action.payload.totalPages || 1,
          totalPlayers: action.payload.pagination?.totalCount || action.payload.totalPlayers || 0,
          limit: action.payload.pagination?.limit || action.payload.limit || 20,
          hasMore: action.payload.pagination?.hasMore || false,
        };
        state.error = null;
      })
      .addCase(fetchCoachTeamRoster.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Load more roster
      .addCase(loadMoreCoachTeamRoster.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadMoreCoachTeamRoster.fulfilled, (state, action) => {
        state.loading = false;
        const newPlayers = action.payload.players || action.payload.roster || [];
        const existingIds = new Set(state.teamRoster.map(p => p._id));
        const uniqueNewPlayers = newPlayers.filter(p => !existingIds.has(p._id));
        state.teamRoster = [...state.teamRoster, ...uniqueNewPlayers];
        state.rosterPagination = {
          currentPage: action.payload.pagination?.currentPage || action.payload.currentPage || 1,
          totalPages: action.payload.pagination?.totalPages || action.payload.totalPages || 1,
          totalPlayers: action.payload.pagination?.totalCount || action.payload.totalPlayers || 0,
          limit: action.payload.pagination?.limit || action.payload.limit || 20,
          hasMore: action.payload.pagination?.hasMore || false,
        };
        state.error = null;
      })
      .addCase(loadMoreCoachTeamRoster.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle follow/unfollow from coachProfileSlice for optimistic updates
      .addCase(followPlayer.pending, (state, action) => {
        const playerId = action.meta.arg;
        // Optimistically update isFollowing in teamRoster
        const player = state.teamRoster.find(p => p._id === playerId);
        if (player) {
          player.isFollowing = true;
        }
        // Also update in teamPlayers if it exists
        const teamPlayer = state.teamPlayers.find(p => p._id === playerId);
        if (teamPlayer) {
          teamPlayer.isFollowing = true;
        }
      })
      .addCase(followPlayer.rejected, (state, action) => {
        const playerId = action.meta.arg;
        // Revert optimistic update on error
        const player = state.teamRoster.find(p => p._id === playerId);
        if (player) {
          player.isFollowing = false;
        }
        const teamPlayer = state.teamPlayers.find(p => p._id === playerId);
        if (teamPlayer) {
          teamPlayer.isFollowing = false;
        }
      })
      .addCase(unfollowPlayer.pending, (state, action) => {
        const playerId = action.meta.arg;
        // Optimistically update isFollowing in teamRoster
        const player = state.teamRoster.find(p => p._id === playerId);
        if (player) {
          player.isFollowing = false;
        }
        // Also update in teamPlayers if it exists
        const teamPlayer = state.teamPlayers.find(p => p._id === playerId);
        if (teamPlayer) {
          teamPlayer.isFollowing = false;
        }
      })
      .addCase(unfollowPlayer.rejected, (state, action) => {
        const playerId = action.meta.arg;
        // Revert optimistic update on error
        const player = state.teamRoster.find(p => p._id === playerId);
        if (player) {
          player.isFollowing = true;
        }
        const teamPlayer = state.teamPlayers.find(p => p._id === playerId);
        if (teamPlayer) {
          teamPlayer.isFollowing = true;
        }
      });
  },
});

export const { setSelectedTeam, setSelectedCoachTeam, clearTeamError, clearTeamData } = teamSlice.actions;
export default teamSlice.reducer;
